/**
 * AJAX Filter Manager
 *
 * Handles dynamic filtering of posts without page refresh using REST API
 */

class FilterManager {
  constructor() {
    this.form = document.querySelector('#program-filter');
    this.resultsContainer = document.querySelector('.col_post .row');
    this.resultsHeading = document.querySelector('.post_wrapper .heading');
    this.paginationContainer = document.querySelector('.col_post .col-pagination');
    this.searchInput = document.querySelector('[name="s"]');
    this.searchForm = document.querySelector('.search_form form');
    this.abortController = null;
    this.searchDebounceTimer = null;
    this.historyDebounceTimer = null;
    this.isRequestInProgress = false;
    this.searchCursorPosition = 0;

    // Get REST API endpoint from localized script
    this.apiUrl = window.peoplrFilters?.apiUrl || '/wp-json/peoplr/v1/filter';

    if (this.form) {
      this.init();
    }
  }

  /**
   * Initialize event listeners
   */
  init() {
    // Prevent default form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
    });

    // Checkbox change events (with event delegation)
    this.form.addEventListener('change', (e) => {
      if (e.target.type === 'checkbox') {
        this.handleFilterChange();
      }
    });

    // Prevent search form from navigating away
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        // Trigger search via AJAX instead
        this.fetchAndUpdateResults(false);
      });
    }

    // Search input with debounce
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        // Store cursor position before debounce
        this.searchCursorPosition = this.searchInput.selectionStart;
        this.debounceSearch();
      });
    }

    // Pagination clicks (event delegation on document since pagination is dynamically added)
    document.addEventListener('click', (e) => {
      const paginationLink = e.target.closest('.col-pagination a');
      if (paginationLink) {
        e.preventDefault();
        this.handlePaginationClick(paginationLink);
      }
    });

    // Browser back/forward buttons
    window.addEventListener('popstate', (e) => {
      this.handlePopState(e);
    });

    // Load initial state from URL on page load
    this.loadInitialState();
  }

  /**
   * Handle checkbox filter changes
   */
  async handleFilterChange() {
    // Clear any pending history update
    clearTimeout(this.historyDebounceTimer);

    // Debounce history updates (300ms)
    this.historyDebounceTimer = setTimeout(() => {
      this.fetchAndUpdateResults(false); // isSearchTyping = false
    }, 300);
  }

  /**
   * Debounce search input
   */
  debounceSearch() {
    clearTimeout(this.searchDebounceTimer);

    // Mark that we should restore focus after update
    this.shouldRestoreSearchFocus = (document.activeElement === this.searchInput);

    this.searchDebounceTimer = setTimeout(() => {
      this.fetchAndUpdateResults(true); // isSearchTyping = true
    }, 300);
  }

  /**
   * Handle pagination link clicks
   *
   * @param {HTMLElement} link Pagination link element
   */
  handlePaginationClick(link) {
    const url = new URL(link.href);

    // Extract page number from path like /page/3/ or from query params
    let page = 1;
    const pathMatch = url.pathname.match(/\/page\/(\d+)\/?/);
    if (pathMatch) {
      page = parseInt(pathMatch[1], 10);
    } else {
      // Fallback to query params
      page = parseInt(url.searchParams.get('paged') || url.searchParams.get('page') || 1, 10);
    }

    const params = this.getFilterParams();
    params.page = page;

    this.fetchResults(params);
    this.updateURL(params);
  }

  /**
   * Get current filter parameters
   *
   * @returns {Object} Filter parameters
   */
  getFilterParams() {
    return {
      cat: this.getCheckedValues('[name="check_cat[]"]'),
      size: this.getCheckedValues('[name="check_size[]"]'),
      search: this.searchInput?.value || '',
      page: 1, // Reset to page 1 on filter change
    };
  }

  /**
   * Get checked checkbox values
   *
   * @param {string} selector Checkbox selector
   * @returns {Array} Array of checked values
   */
  getCheckedValues(selector) {
    const checkboxes = this.form.querySelectorAll(selector);
    return Array.from(checkboxes)
      .filter(cb => cb.checked)
      .map(cb => cb.value);
  }

  /**
   * Fetch results and update DOM
   *
   * @param {boolean} isSearchTyping Whether this is triggered by search typing
   */
  async fetchAndUpdateResults(isSearchTyping = false) {
    const params = this.getFilterParams();
    params.isSearchTyping = isSearchTyping;

    try {
      await this.fetchResults(params);
      this.updateURL(params);
    } catch (error) {
      console.error('Filter error:', error);
    }
  }

  /**
   * Fetch filtered results from REST API
   *
   * @param {Object} params Filter parameters
   * @param {number} retryCount Current retry attempt
   */
  async fetchResults(params, retryCount = 0) {
    // Prevent duplicate requests
    if (this.isRequestInProgress) {
      return;
    }

    // Cancel any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    this.isRequestInProgress = true;

    // Show loading state
    this.showLoading();

    try {
      const url = this.buildURL(params);
      const response = await fetch(url, {
        method: 'GET',
        signal: this.abortController.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait a moment.');
        }
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();

      // Update DOM with results
      this.updateDOM(data);

      // Announce to screen readers
      this.announceResults(data.count);

      // Move focus to results (but not during search typing to avoid disrupting user)
      if (!params.isSearchTyping) {
        this.manageFocus();
      }

    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError') {
        return;
      }

      // Retry with exponential backoff
      if (retryCount < 3) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchResults(params, retryCount + 1);
      }

      // Show error after all retries
      this.showError(error.message);

      // Fallback to form submission after errors
      if (retryCount >= 3) {
        this.fallbackToFormSubmit();
      }

    } finally {
      this.isRequestInProgress = false;
      this.hideLoading();
    }
  }

  /**
   * Build REST API URL with parameters
   *
   * @param {Object} params Filter parameters
   * @returns {string} Full API URL
   */
  buildURL(params) {
    const url = new URL(this.apiUrl, window.location.origin);

    if (params.cat && params.cat.length) {
      url.searchParams.set('filter_cat', params.cat.join('-'));
    }

    if (params.size && params.size.length) {
      url.searchParams.set('filter_topics', params.size.join('-'));
    }

    if (params.search) {
      url.searchParams.set('s', params.search);
    }

    if (params.page) {
      url.searchParams.set('page', params.page);
    }

    return url.toString();
  }

  /**
   * Update DOM with filtered results
   *
   * @param {Object} data Response data from API
   */
  updateDOM(data) {
    if (!this.resultsContainer) {
      return;
    }

    // Fade out
    this.resultsContainer.style.opacity = '0';

    setTimeout(() => {
      // Update results
      this.resultsContainer.innerHTML = data.html;

      // Update heading
      if (this.resultsHeading && data.count !== undefined) {
        const countText = data.count === 1 ? '1 result' : `${data.count} results`;
        this.resultsHeading.textContent = `We've Filtered Your Results (${countText})`;
      }

      // Update pagination
      this.updatePagination(data.pagination_html);

      // Fade in
      this.resultsContainer.style.opacity = '1';

      // Restore search input focus and cursor position if it was focused before update
      if (this.shouldRestoreSearchFocus && this.searchInput) {
        this.searchInput.focus();
        this.searchInput.setSelectionRange(this.searchCursorPosition, this.searchCursorPosition);
        this.shouldRestoreSearchFocus = false;
      }

      // Trigger event for matchHeight re-initialization
      if (window.jQuery) {
        window.jQuery(document).trigger('postsUpdated');
      }

    }, 200);
  }

  /**
   * Update pagination HTML
   *
   * @param {string} paginationHtml Pagination HTML from API
   */
  updatePagination(paginationHtml) {
    // Find existing pagination row or the parent container
    const existingPaginationRow = document.querySelector('.col_post .row.col-pagination, .col_post > .row:has(.col-pagination)');
    const colPost = document.querySelector('.col_post');

    if (!colPost) {
      return;
    }

    // Remove existing pagination if it exists
    if (existingPaginationRow) {
      existingPaginationRow.remove();
    }

    // Add new pagination if provided
    if (paginationHtml && paginationHtml.trim()) {
      colPost.insertAdjacentHTML('beforeend', paginationHtml);
    }
  }

  /**
   * Show loading state
   */
  showLoading() {
    if (!this.resultsContainer) {
      return;
    }

    // Add loading class and aria-busy
    this.resultsContainer.classList.add('is-loading');
    this.resultsContainer.setAttribute('aria-busy', 'true');

    // Disable filter checkboxes during loading
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(cb => {
      cb.disabled = true;
    });

    // Add inline spinner
    const spinner = document.createElement('div');
    spinner.className = 'filter-loading-spinner';
    spinner.innerHTML = '<div class="spinner"></div>';
    spinner.setAttribute('role', 'status');
    spinner.setAttribute('aria-live', 'polite');
    spinner.innerHTML += '<span class="sr-only">Loading results...</span>';

    this.resultsContainer.insertAdjacentElement('beforebegin', spinner);
  }

  /**
   * Hide loading state
   */
  hideLoading() {
    if (!this.resultsContainer) {
      return;
    }

    // Remove loading class and aria-busy
    this.resultsContainer.classList.remove('is-loading');
    this.resultsContainer.removeAttribute('aria-busy');

    // Re-enable filter checkboxes
    const checkboxes = this.form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(cb => {
      cb.disabled = false;
    });

    // Remove spinner
    const spinner = document.querySelector('.filter-loading-spinner');
    if (spinner) {
      spinner.remove();
    }
  }

  /**
   * Update URL using History API
   *
   * @param {Object} params Filter parameters
   */
  updateURL(params) {
    const url = new URL(window.location);

    // Update query params
    if (params.cat && params.cat.length) {
      url.searchParams.set('filter_cat', params.cat.join('-'));
    } else {
      url.searchParams.delete('filter_cat');
    }

    if (params.size && params.size.length) {
      url.searchParams.set('filter_topics', params.size.join('-'));
    } else {
      url.searchParams.delete('filter_topics');
    }

    if (params.search) {
      url.searchParams.set('s', params.search);
    } else {
      url.searchParams.delete('s');
    }

    // Update page parameter
    if (params.page && params.page > 1) {
      url.searchParams.set('paged', params.page);
    } else {
      url.searchParams.delete('paged');
    }

    // Use pushState for filter changes, replaceState for search typing
    const method = params.isSearchTyping ? 'replaceState' : 'pushState';
    history[method]({ filters: params }, '', url.toString());
  }

  /**
   * Handle browser back/forward buttons
   *
   * @param {PopStateEvent} event PopState event
   */
  handlePopState(event) {
    if (event.state && event.state.filters) {
      // Restore filters from state
      this.restoreFiltersFromState(event.state.filters);

      // Fetch results
      this.fetchResults(event.state.filters);
    } else {
      // No state - reload page
      window.location.reload();
    }
  }

  /**
   * Restore filter checkboxes from state
   *
   * @param {Object} filters Filter state
   */
  restoreFiltersFromState(filters) {
    // Restore category checkboxes
    const catCheckboxes = this.form.querySelectorAll('[name="check_cat[]"]');
    catCheckboxes.forEach(cb => {
      cb.checked = filters.cat && filters.cat.includes(cb.value);
    });

    // Restore topic checkboxes
    const topicCheckboxes = this.form.querySelectorAll('[name="check_size[]"]');
    topicCheckboxes.forEach(cb => {
      cb.checked = filters.size && filters.size.includes(cb.value);
    });

    // Restore search input
    if (this.searchInput && filters.search) {
      this.searchInput.value = filters.search;
    }
  }

  /**
   * Load initial state from URL parameters
   */
  loadInitialState() {
    const urlParams = new URLSearchParams(window.location.search);

    // Check if any filter params exist in URL
    const hasCat = urlParams.has('filter_cat');
    const hasTopics = urlParams.has('filter_topics');
    const hasSearch = urlParams.has('s');
    const hasPage = urlParams.has('paged');

    // Only set initial state if we have params
    if (hasCat || hasTopics || hasSearch || hasPage) {
      const initialState = {
        cat: hasCat ? urlParams.get('filter_cat').split('-') : [],
        size: hasTopics ? urlParams.get('filter_topics').split('-') : [],
        search: hasSearch ? urlParams.get('s') : '',
        page: hasPage ? parseInt(urlParams.get('paged'), 10) : 1,
      };

      // Set initial history state
      history.replaceState({ filters: initialState }, '');
    }
  }

  /**
   * Announce results to screen readers
   *
   * @param {number} count Number of results
   */
  announceResults(count) {
    const message = (count === 0) ? 'No results found. Try adjusting your filters.' : `Found ${count} ${count === 1 ? 'result' : 'results'}`;

    // Use WordPress a11y.speak if available
    if (typeof wp !== 'undefined' && wp.a11y && wp.a11y.speak) {
      wp.a11y.speak(message, 'polite');
    } else {
      // Fallback: update ARIA live region
      const liveRegion = document.querySelector('.filter-aria-live');
      if (liveRegion) {
        liveRegion.textContent = message;
      }
    }
  }

  /**
   * Move focus to results heading for keyboard users
   */
  manageFocus() {
    if (this.resultsHeading) {
      this.resultsHeading.setAttribute('tabindex', '-1');
      this.resultsHeading.focus();

      // Remove tabindex after focus
      setTimeout(() => {
        this.resultsHeading.removeAttribute('tabindex');
      }, 100);
    }
  }

  /**
   * Show error message
   *
   * @param {string} message Error message
   */
  showError(message) {
    // Remove any existing error
    const existingError = document.querySelector('.filter-error');
    if (existingError) {
      existingError.remove();
    }

    // Create error element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'filter-error alert alert-danger';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.innerHTML = `
      <strong>Error:</strong> ${message}
      <button class="retry-btn btn btn-sm btn-danger">Retry</button>
    `;

    // Add retry button handler
    const retryBtn = errorDiv.querySelector('.retry-btn');
    retryBtn.addEventListener('click', () => {
      errorDiv.remove();
      this.fetchAndUpdateResults(false);
    });

    // Insert error before results
    if (this.resultsContainer) {
      this.resultsContainer.insertAdjacentElement('beforebegin', errorDiv);
    }

    // Announce error to screen readers
    if (typeof wp !== 'undefined' && wp.a11y && wp.a11y.speak) {
      wp.a11y.speak(message, 'assertive');
    }
  }

  /**
   * Fallback to standard form submission
   */
  fallbackToFormSubmit() {
    // Re-enable default form submission
    this.form.removeEventListener('submit', this.preventSubmit);

    // Submit form the old-fashioned way
    this.form.submit();
  }

  /**
   * Prevent default form submit (bound handler)
   */
  preventSubmit(e) {
    e.preventDefault();
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FilterManager();
  });
} else {
  new FilterManager();
}
